


















import { Component, Vue, Watch } from 'vue-property-decorator'
import BolhaDoChatDeSuporte from './BolhaDoChatDeSuporte.vue'
import PainelDoChatDeSuporte from './PainelDoChatDeSuporte.vue'
import UserLoginStore from '@/store/vuex/authentication/UserLoginStore'
import { chatDeSuporteConfigurado, ErroDoChatDeSuporte } from '@/api/suporteChat'
import {
	abrirChatDeSuporte,
	carregarIntervaloDoChat,
	ChatDeSuporteModule,
	conectarChatDeSuporte,
	consultarChatDeSuporte,
	faseDeErro,
	marcarChatDeSuporteComoLido,
	sessaoVivaDe,
} from '@/store/vuex/suporte/ChatDeSuporteStore'
import { atrasoDoPolling, conversaAberta, encolherAte, ultimaMensagemDoSuporte } from '@/shareds/chat-de-suporte'

const POLLING_COM_BOLHA_MS = 15_000
const POLLING_EM_ESPERA_MS = 30_000
const ENCOLHER_MS = 420
const SINAL_NO_ICONE_MS = 700

const temTamanho = (elemento: Element) => {
	const caixa = elemento.getBoundingClientRect()
	return caixa.width > 0 && caixa.height > 0
}

/**
 * Almode Chat no ERP (a mesma experiência do app Almode): painel da conversa e bolha flutuante, os dois lendo o
 * mesmo store. Este componente é o dono do polling e da sessão; a sessão só abre quando a pessoa clica em
 * "Falar com o suporte", porque cada sessão registra um contato no suporte.
 */
@Component({
	components: {
		BolhaDoChatDeSuporte,
		PainelDoChatDeSuporte,
	},
})
export default class ChatDeSuporte extends Vue {
	configurado = chatDeSuporteConfigurado()
	agora = Date.now()
	falhas = 0
	temporizador?: number

	get dono(): string | null {
		return (UserLoginStore.usuario && UserLoginStore.usuario.id) || null
	}

	get vivo() {
		return sessaoVivaDe(this.dono, this.agora)
	}

	get painelAberto() {
		return ChatDeSuporteModule.painelAberto
	}

	get conversaEmAndamento() {
		return conversaAberta(ChatDeSuporteModule.estado)
	}

	get bolhaElegivel() {
		return this.vivo && this.conversaEmAndamento && !this.painelAberto
	}

	get bolhaVisivel() {
		return this.bolhaElegivel && ChatDeSuporteModule.ocultaAte === null
	}

	get idDaUltimaDoSuporte() {
		const ultima = ultimaMensagemDoSuporte(ChatDeSuporteModule.itens)
		return ultima ? ultima.id : 0
	}

	get escuro() {
		return this.$vuetify.theme.dark
	}

	mounted() {
		document.addEventListener('visibilitychange', this.aoMudarVisibilidade)
		this.agendar()
	}

	beforeDestroy() {
		document.removeEventListener('visibilitychange', this.aoMudarVisibilidade)
		window.clearTimeout(this.temporizador)
		// Saiu do ERP: a conversa não fica na memória para o próximo usuário
		ChatDeSuporteModule.esquecerTudo()
	}

	@Watch('dono')
	aoTrocarDeUsuario(dono: string | null) {
		if (ChatDeSuporteModule.dono && ChatDeSuporteModule.dono !== dono) ChatDeSuporteModule.esquecerTudo()
	}

	// "Ocultar" vale até a próxima mensagem do suporte, e conversa encerrada recomeça com a bolha visível
	@Watch('idDaUltimaDoSuporte')
	aoChegarMensagemDoSuporte(id: number) {
		const ocultaAte = ChatDeSuporteModule.ocultaAte
		if (ocultaAte !== null && id > ocultaAte) ChatDeSuporteModule.ocultarAte(null)
	}

	@Watch('conversaEmAndamento')
	aoMudarConversa(emAndamento: boolean) {
		if (!emAndamento) ChatDeSuporteModule.ocultarAte(null)
	}

	@Watch('painelAberto')
	aoMudarPainel(aberto: boolean) {
		this.falhas = 0
		ChatDeSuporteModule.setSemConexao(false)
		if (aberto) this.garantirSessao()
		else this.agendar()
	}

	@Watch('bolhaVisivel')
	@Watch('vivo')
	aoMudarSituacao() {
		this.agendar()
	}

	abrirPelaBolha() {
		abrirChatDeSuporte(null)
	}

	fecharPainel() {
		ChatDeSuporteModule.fecharPainel()
	}

	/**
	 * Fechar e minimizar encolhem o painel até onde o chat reabre: a bolha, quando há conversa aberta, ou o ícone da
	 * barra. Sem destino na tela, ou com movimento reduzido, o painel só some.
	 */
	encolherPainel(elemento: Element, terminar: () => void) {
		const painel = elemento as HTMLElement
		if (typeof painel.animate !== 'function') {
			terminar()
			return
		}
		// A bolha só entra na tela depois que o painel sai do estado: o destino é medido no quadro seguinte
		window.requestAnimationFrame(() => {
			const destino = this.destinoAoFechar()
			const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches
			const animacao = !destino || reduzido
				? painel.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'ease', fill: 'forwards' })
				: painel.animate(this.quadrosDeEncolher(painel, destino), {
					duration: ENCOLHER_MS,
					easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
					fill: 'forwards',
				})
			animacao.onfinish = () => {
				terminar()
				if (destino && !reduzido && destino.hasAttribute('data-chat-suporte-icone')) this.sinalizarIcone(destino)
			}
			animacao.oncancel = () => terminar()
		})
	}

	destinoAoFechar(): HTMLElement | null {
		const bolha = this.$el.querySelector<HTMLElement>('.chat-bolha')
		if (bolha && temTamanho(bolha)) return bolha
		const icones = Array.from(document.querySelectorAll<HTMLElement>('[data-chat-suporte-icone]'))
		return icones.find(temTamanho) || null
	}

	quadrosDeEncolher(painel: HTMLElement, destino: HTMLElement): Keyframe[] {
		const { x, y, escalaX, escalaY } = encolherAte(painel.getBoundingClientRect(), destino.getBoundingClientRect())
		return [
			{ transform: 'translate(0, 0) scale(1, 1)', borderRadius: '0px', opacity: 1 },
			{ opacity: 1, offset: 0.7 },
			{ transform: `translate(${x}px, ${y}px) scale(${escalaX}, ${escalaY})`, borderRadius: '50%', opacity: 0 },
		]
	}

	/** O ícone da barra responde quando o painel chega nele, para a pessoa ver por onde o chat reabre. */
	sinalizarIcone(icone: HTMLElement) {
		icone.classList.remove('chat-icone--recebeu')
		// Lê o layout para o navegador reiniciar a animação, se ela ainda estiver rodando
		void icone.offsetWidth
		icone.classList.add('chat-icone--recebeu')
		window.setTimeout(() => icone.classList.remove('chat-icone--recebeu'), SINAL_NO_ICONE_MS)
	}

	consultarAgora() {
		this.agendar(true)
	}

	async garantirSessao() {
		const dono = this.dono
		if (!dono) return
		if (!sessaoVivaDe(dono)) {
			ChatDeSuporteModule.setFase({ tipo: 'conectando' })
			try {
				await carregarIntervaloDoChat()
				await conectarChatDeSuporte(dono)
			} catch (erro) {
				ChatDeSuporteModule.setFase(faseDeErro(erro))
				return
			}
		}
		ChatDeSuporteModule.setFase({ tipo: 'pronto' })
		// Abrir o painel busca o histórico inteiro, sem tirar da tela o que já está nela
		ChatDeSuporteModule.reiniciarCursor()
		this.agora = Date.now()
		this.agendar(true)
	}

	/** 401 com o painel aberto: sessão nova sem tirar a conversa da tela. */
	async renovarSessao() {
		ChatDeSuporteModule.descartarSessao()
		if (this.painelAberto) await this.garantirSessao()
	}

	aoMudarVisibilidade() {
		if (document.hidden) window.clearTimeout(this.temporizador)
		else this.agendar(true)
	}

	intervalo(): number | null {
		if (!this.vivo || document.hidden) return null
		if (this.painelAberto) {
			return ChatDeSuporteModule.fase.tipo === 'pronto' ? atrasoDoPolling(ChatDeSuporteModule.pollMs, this.falhas) : null
		}
		return this.bolhaVisivel ? POLLING_COM_BOLHA_MS : POLLING_EM_ESPERA_MS
	}

	agendar(agora = false) {
		window.clearTimeout(this.temporizador)
		const espera = this.intervalo()
		if (espera === null) return
		this.temporizador = window.setTimeout(this.rodada, agora ? 0 : espera)
	}

	async rodada() {
		this.agora = Date.now()
		if (this.vivo && !document.hidden) {
			try {
				await consultarChatDeSuporte()
				this.falhas = 0
				ChatDeSuporteModule.setSemConexao(false)
				if (this.painelAberto) marcarChatDeSuporteComoLido()
			} catch (erro) {
				if (erro instanceof ErroDoChatDeSuporte && erro.status === 401) {
					// Sessão vencida: some a bolha. Com o painel aberto, a pessoa está conversando e ganha sessão nova.
					await this.renovarSessao()
				} else {
					// Erro de rede: tenta de novo no próximo ciclo, sem aviso em loop
					this.falhas += 1
					if (this.painelAberto) ChatDeSuporteModule.setSemConexao(true)
				}
			}
		}
		this.agora = Date.now()
		this.agendar()
	}
}
